// One table cell summarising a diploma's landing page: status chip, the
// "Not campaign-ready: missing …" line and the new-leads badge (§D.1). Used by
// the Diplomas list (Landing column) and the Diploma landing pages list.
import { Link as RouterLink } from 'react-router-dom';
import { Link, Stack, Typography } from '@mui/material';
import Label from 'src/components/Label';
import type { LandingSummaryRow } from '../useLandingSummaries';
import { LEADS_WINDOW_DAYS, landingStatusChip, notCampaignReady } from '../rules/labels';

type Props = {
  summary?: LandingSummaryRow | null;
  // false while the summaries request is in flight or failed
  loaded?: boolean;
  // When set, the status chip links to the landing editor.
  to?: string;
};

export default function LandingSummaryCell({ summary, loaded = true, to }: Props) {
  if (!summary) {
    return (
      <Typography variant="caption" color="text.disabled">
        {loaded ? 'No landing data' : '…'}
      </Typography>
    );
  }
  const chip = landingStatusChip(
    summary.status,
    summary.requiredMet,
    summary.requiredTotal,
    false,
    summary.active
  );
  // An inactive diploma has no public page: "campaign-ready" does not apply.
  const missing = summary.active === false ? '' : notCampaignReady(summary.missingRequired);
  return (
    <Stack spacing={0.5} alignItems="flex-start" sx={{ minWidth: 180 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
        {to ? (
          <Link component={RouterLink} to={to} underline="none" aria-label={`${chip.label}: open the landing page editor`}>
            <Label
              color={chip.color}
              variant={chip.color === 'default' ? 'ghost' : 'filled'}
              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            >
              {chip.label}
            </Label>
          </Link>
        ) : (
          <Label color={chip.color} variant={chip.color === 'default' ? 'ghost' : 'filled'}>
            {chip.label}
          </Label>
        )}
        {summary.leadsSince > 0 && (
          <Label color="info" variant="ghost">
            {summary.leadsSince} new {summary.leadsSince === 1 ? 'lead' : 'leads'}
          </Label>
        )}
      </Stack>
      {!!summary.leads30Days && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          Leads ({LEADS_WINDOW_DAYS} days): {summary.leads30Days}
        </Typography>
      )}
      {missing && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {missing}
        </Typography>
      )}
    </Stack>
  );
}
